/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectionStrategy, Component, Input, OnInit, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {UIRouterModule} from '@uirouter/angular';
import * as _ from 'lodash';
import template from './external-ad-units.component.html';

import {AdnSelectSearchElement} from './adn-select/adn-select.component';
import {ExternalAdUnitApiService} from '../../external-ad-unit/external-ad-unit-api.service';
import {SearchApiService} from '../controller/search-api.service';
import {APP_SHARED} from '../app-shared';
import {AdnSelectComponent} from './adn-select/adn-select.component';

export interface ExternalAdUnitsHook {
  model: any;
  editsMade: () => void;
  initExtAdUnits?: () => void;
  beforeSubmitExtAdUnit?: () => void;
}

@Component({
  standalone: true,
  imports: [...APP_SHARED, FormsModule, TranslateModule, UIRouterModule, AdnSelectComponent],
  selector: 'adn-external-ad-units',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExternalAdUnitsComponent implements OnInit {
  @Input() hook: ExternalAdUnitsHook;
  @Input() translation = 'adUnit';

  extAdUnitTrans = 'adUnit';
  readonly populatedExternalAdUnits = signal<any[]>([]);
  readonly externalAdUnitResults = signal<any[]>([]);
  readonly hasExternalAdUnits = signal(true);

  constructor(
    private readonly externalAdUnitApi: ExternalAdUnitApiService,
    private readonly searchApi: SearchApiService
  ) {}

  ngOnInit(): void {
    this.extAdUnitTrans = this.translation || 'adUnit';

    this.hook.initExtAdUnits = () => this.initExtAdUnits();
    this.hook.beforeSubmitExtAdUnit = () => this.beforeSubmitExtAdUnit();

    if (this.hook.model) {
      this.initExtAdUnits();
    }

    this.hasExternalAdUnits.set(true);
    if (!this.hook.model?.externalAdUnits?.length) {
      this.externalAdUnitApi.listOptions({pageSize: 1}).subscribe((eauPage) => {
        // Macrotask — signal write in a microtask re-enters synchronize (NG0103).
        setTimeout(() => {
          this.hasExternalAdUnits.set(!!eauPage.results.length);
        }, 0);
      });
    }
  }

  translationKey(suffix: string): string {
    return 'externalAdUnit.' + this.extAdUnitTrans + suffix;
  }

  initExtAdUnits(): void {
    if (!this.hook?.model) {
      this.populatedExternalAdUnits.set([]);
      return;
    }

    const externalAdUnits = this.hook.model.externalAdUnits || [];
    const externalAdUnitIds = _.map(externalAdUnits, 'id');
    // Unresolved stubs — spinner [resolved] stays false until hydrate settles.
    this.populatedExternalAdUnits.set(_.map(externalAdUnits, (eau: any) => ({
      ...eau,
      resolved: false
    })));
    this.externalAdUnitApi.getMany(externalAdUnitIds).subscribe((extAdUnitsPage) => {
      const next = _.map(extAdUnitsPage.results || [], (eau: any) => ({
        ...eau,
        currentId: eau.id,
        resolved: true
      }));
      // Macrotask — signal write in a microtask re-enters synchronize (NG0103).
      setTimeout(() => {
        this.populatedExternalAdUnits.set(next);
      }, 0);
    });
  }

  searchExternalAdUnits = (searchElement: AdnSelectSearchElement): void => {
    this.searchApi.search({q: searchElement.search, types: 'ExternalAdUnit'}).subscribe((data) => {
      this.externalAdUnitResults.set(data.searchResults || []);
    });
  };

  editableExternalAdUnit(index: number, isEditable: boolean): void {
    this.populatedExternalAdUnits.update((units) => {
      const next = units.slice();
      const extAdUnit = {...next[index], editable: isEditable};
      if (!extAdUnit.name) {
        next.splice(index, 1);
      } else {
        next[index] = extAdUnit;
      }
      return next;
    });
  }

  extAdUnitSelected(index: number): void {
    const units = this.populatedExternalAdUnits();
    const selId = units[index]?.currentId;
    const theAdUnit = _.find(this.externalAdUnitResults(), ['id', selId]);
    if (!theAdUnit) {
      return;
    }
    this.populatedExternalAdUnits.update((list) => {
      const next = list.slice();
      next[index] = {...theAdUnit, currentId: selId, resolved: false};
      return next;
    });
    this.externalAdUnitApi.get(selId).subscribe({
      next: (extAdUnit) => {
        setTimeout(() => {
          this.populatedExternalAdUnits.update((list) => {
            const next = list.slice();
            next[index] = {...extAdUnit, currentId: selId, resolved: true};
            return next;
          });
        }, 0);
      },
      error: () => {
        setTimeout(() => {
          this.populatedExternalAdUnits.update((list) => {
            const next = list.slice();
            if (next[index]) {
              next[index] = {...next[index], resolved: true};
            }
            return next;
          });
        }, 0);
      }
    });
  }

  addExternalAdUnit(): void {
    this.hook.editsMade();
    this.populatedExternalAdUnits.update((units) => [
      {resolved: true, editable: true},
      ...units
    ]);
  }

  deleteExternalAdUnit(index: number): void {
    this.hook.editsMade();
    this.populatedExternalAdUnits.update((units) => {
      const next = units.slice();
      next.splice(index, 1);
      return next;
    });
  }

  onExternalAdUnitIdChange(index: number, currentId: any): void {
    this.populatedExternalAdUnits.update((units) => {
      const next = units.slice();
      next[index] = {...next[index], currentId};
      return next;
    });
  }

  beforeSubmitExtAdUnit(): void {
    this.hook.model.externalAdUnits = [];
    _.forEach(this.populatedExternalAdUnits(), (eau: any) => {
      if (!eau.id) {
        return;
      }
      this.hook.model.externalAdUnits.push({
        id: eau.id
      });
    });
  }

  trackByIndex(index: number): number {
    return index;
  }
}
